import { Trans } from '~/common/context';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormContainer, notify, Text } from '@wyz/ui';
import { OFFER_TYPE, useBoolean } from '@wyz/utils';
import { debounce, isNil, sum } from 'lodash-es';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { useManageCart } from '~/purchase/api';
import {
  PurchaseAddToCartFailureModal,
  PurchaseOfferErrorRow,
  PurchaseOfferSuccessRow,
  PurchaseTable,
} from '~/purchase/components';
import {
  CartUtils,
  MIN_QUANTITY,
  PurchaseSearchHelpers,
} from '~/purchase/helpers';

import { PurchaseErrorCodes } from '~/common/helpers';
import { getListQuantitySchema } from '~/purchase/schemas';
import { IOffer } from '~/purchase/types';
import { IUserCustomer } from '~/common/types';
import { ITyreSupplier } from '@wyz/types';
import { IBaseTyreOffer } from '~/purchase/types';

type Props = {
  product: IBaseTyreOffer;
  requestedQuantity: number;
  customer: IUserCustomer;
  onRefreshTyre: (ean: string) => Promise<Array<IOffer>>;
};
function TyreGroupRow({
  product,
  requestedQuantity,
  customer,
  onRefreshTyre,
}: Props) {
  const mappedOffers: Array<IOffer> = PurchaseSearchHelpers.mapOffers(
    product.suppliers,
  );
  const cart = useManageCart({
    context: { customerId: customer.id },
    get: { enabled: true },
    patch: { enabled: true },
    add: { enabled: true },
  });
  const quantitySchema = getListQuantitySchema(
    mappedOffers.map((offer) => ({
      quantity: offer.offerMetadata.availableQuantity,
      isParent:
        offer.offerMetadata.offerType === OFFER_TYPE.DISTRIBUTOR_PLATFORM,
    })),
    MIN_QUANTITY,
    requestedQuantity,
  );
  const defaultValues = mappedOffers.map(
    (offer) => offer.offerMetadata.proposedQuantity,
  );

  const methods = useForm({
    resolver: zodResolver(quantitySchema),
    mode: 'all',
    reValidateMode: 'onChange',
    defaultValues: {
      quantity: defaultValues,
    },
  });

  const [addToCartFailureOpened, addToCartFailureHandlers] = useBoolean();
  const cartItems =
    cart.get.data?.data.items.filter(
      (item) => item.tyre.ean === product.tyre.ean,
    ) || [];

  const onUpdateQuantity = (supplier: ITyreSupplier) =>
    debounce((newQuantity?: number | null) => {
      // Update the cart ONLY if the user has already added this row to cart
      if (cartItems.length > 0) {
        const supplierCartItem = cartItems.find(
          (item) => item.supplier.id === supplier.id,
        );
        if (supplierCartItem) {
          cart.patch.mutate({
            itemId: supplierCartItem.id,
            quantity: newQuantity || 0,
          });
        }
      }
    }, 2000);

  return (
    <React.Fragment>
      <FormContainer
        methods={methods}
        submitHandler={(formData) => {
          const quantities = formData.quantity.filter((value) => !isNil(value));
          const totalQuantityPerTyre = sum(quantities);
          if (totalQuantityPerTyre === 0) {
            notify.warning(
              <Text id='empty-quantity-warning'>
                <Trans i18nKey='app.purchase.cart.errors.empty_quantity' />
              </Text>,
              { id: 'toast-warning-purchase-cart-empty-quantity' },
            );
          } else {
            cart.add.mutate(
              CartUtils.prepareAddToCartPayload(
                product.tyre,
                mappedOffers,
                quantities,
              ),
              {
                onError: async (error) => {
                  addToCartFailureHandlers.setTrue();
                  if (error.message === PurchaseErrorCodes.ERR_INVALID_CART) {
                    await cart.get.refetch();
                  }
                },
                onSuccess: () => {
                  notify.success(
                    <Trans i18nKey='app.purchase.cart.success.order' />,
                    { id: 'toast-success-purchase-cart-add-to-cart' },
                  );
                },
              },
            );
          }
        }}
        id={product.tyre.ean}
      >
        <PurchaseTable>
          <tbody>
            {product.status === 'AVAILABLE_STOCK' ? (
              mappedOffers.map((offer, offerIndex) => {
                return (
                  <PurchaseOfferSuccessRow
                    key={`${product.tyre.ean}-${offer.supplier.id}-${offerIndex}`}
                    showCommon={offerIndex === 0}
                    offersCount={mappedOffers.length}
                    product={product}
                    offer={offer}
                    requestedQuantity={requestedQuantity}
                    isAdding={cart.add.isLoading}
                    onUpdateQuantity={onUpdateQuantity}
                    offerIndex={offerIndex}
                    customer={customer}
                    onRefreshTyre={onRefreshTyre}
                  />
                );
              })
            ) : (
              <PurchaseOfferErrorRow
                product={product}
                customer={customer}
                showCommon
              />
            )}
          </tbody>
        </PurchaseTable>
      </FormContainer>
      {addToCartFailureOpened && (
        <PurchaseAddToCartFailureModal
          onClose={() => {
            addToCartFailureHandlers.setFalse();
            cart.get.refetch().catch(() => {});
          }}
          error={cart.add.error}
        />
      )}
    </React.Fragment>
  );
}

export default React.memo(TyreGroupRow);
